import { SearchPriceQuoteItemsQuery } from '@/types';
import { useCallback, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useSearchPriceQuoteItemsLazyQuery } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/documents/search-price-quote-items.generated';

export interface QuoteItemSearchResult {
  currencyCode: string;
  itemCode: string | null;
  itemDescription: string | null;
  itemName: string;
  itemPackSize: string | null;
  packPriceInMicros: number;
  perUnitPriceInMicros: number | null;
  variantPackId: string;
}

type RawResult =
  SearchPriceQuoteItemsQuery['searchPriceQuoteItems']['items'][number];

const SEARCH_DEBOUNCE_MS = 500;

/** Server -> client mapping for searchPriceQuoteItems rows. Nullable scalars
 *  are normalized to null so the view layer can treat them uniformly. */
const transformResult = (raw: RawResult): QuoteItemSearchResult => ({
  currencyCode: raw.currency_code,
  itemCode: raw.item_code ?? null,
  itemDescription: raw.item_description ?? null,
  itemName: raw.item_name,
  itemPackSize: raw.item_pack_size ?? null,
  packPriceInMicros: raw.pack_price_in_micros,
  perUnitPriceInMicros: raw.per_unit_price_in_micros ?? null,
  variantPackId: raw.variant_pack_id,
});

interface UseQuoteItemSearchLoaderParams {
  chatId: string;
  onError?: () => void;
}

interface UseQuoteItemSearchLoaderResult {
  hasError: boolean;
  /** True from the moment the rep types until the loader's results catch up
   *  to their current input — covers the debounce window AND in-flight time. */
  isSearching: boolean;
  results: QuoteItemSearchResult[];
  setQuery: (text: string) => void;
}

/**
 * Lazy catalog search for the price-quote builder. Each search runs against
 * `searchPriceQuoteItems` for the chat's supplier catalog. Owns its own debounce
 * and discards stale responses via a per-request id, so only the result for
 * the most recent (chatId, query) pair updates state. `no-cache` because
 * results are search-specific and don't benefit from Apollo's cache.
 */
export const useQuoteItemSearchLoader = ({
  chatId,
  onError,
}: UseQuoteItemSearchLoaderParams): UseQuoteItemSearchLoaderResult => {
  const [results, setResults] = useState<QuoteItemSearchResult[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [lastResolvedQuery, setLastResolvedQuery] = useState('');
  const [hasError, setHasError] = useState(false);
  // Monotonic id incremented on every `executeSearch` / `clear`. Resolved
  // responses are dropped when their captured id is no longer current —
  // catches both stale queries (rep keeps typing) and stale chatIds (rep
  // switches accounts mid-flight).
  const requestIdRef = useRef(0);
  const [fetchItems] = useSearchPriceQuoteItemsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const executeSearch = useCallback(
    (query: string) => {
      const requestId = ++requestIdRef.current;

      fetchItems({ variables: { chatId, query } })
        .then((result) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          // Settle `lastResolvedQuery` on both success and error so
          // `isSearching` flips false and the view can show the right state.
          setLastResolvedQuery(query);
          if (result.error) {
            setHasError(true);
            onError?.();
            return;
          }
          setHasError(false);
          setResults(
            (result.data?.searchPriceQuoteItems?.items ?? []).map(
              transformResult,
            ),
          );
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setLastResolvedQuery(query);
          setHasError(true);
          onError?.();
        });
    },
    [chatId, fetchItems, onError],
  );

  const debouncedExecuteSearch = useDebouncedCallback(
    executeSearch,
    SEARCH_DEBOUNCE_MS,
  );

  const setQuery = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      setCurrentText(trimmed);
      if (trimmed.length === 0) {
        debouncedExecuteSearch.cancel();
        requestIdRef.current++;
        setResults([]);
        setLastResolvedQuery('');
        setHasError(false);
        return;
      }
      debouncedExecuteSearch(trimmed);
    },
    [debouncedExecuteSearch],
  );

  // Comparing `currentText` against `lastResolvedQuery` is more precise than
  // Apollo's `loading` alone: it stays true through the debounce delay and
  // across follow-up keystrokes that invalidate prior results, and flips
  // false once the loader settles (success OR error).
  const isSearching =
    currentText.length > 0 && currentText !== lastResolvedQuery;

  return { hasError, isSearching, results, setQuery };
};
